import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Grid, Paper, Card, CardContent,
  TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, Chip,
  InputAdornment, CircularProgress, Button, Avatar, Divider, IconButton, Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ComputerIcon from '@mui/icons-material/Computer';

const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5001/api').replace(/\/$/, '');

export default function AdminUserActivity() {
  // Navigation State
  const [selectedUserId, setSelectedUserId] = useState(null); 
  // Users Directory States
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Metrics States
  const [metrics, setMetrics] = useState({
    realtimeUsers: 0,
    dauMauRatio: '0.0',
    avgSession: '0m',
    newRegistrations: 0,
    retentionRate: '0%'
  });

  // Selected User Timeline Sub-page States
  const [userData, setUserData] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loadingTimeline, setLoadingTimeline] = useState(false);

  // Status Badge Helper
  const getStatusChip = (status) => {
    return status === 'Active' ? (
      <Chip label="Active" color="success" size="small" variant="soft" sx={{ fontWeight: '600' }} />
    ) : (
      <Chip label="Inactive" color="default" size="small" sx={{ fontWeight: '600' }} />
    );
  };

  // Search Handler
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };
  // =========================================================
  // 1. FETCH METRICS & USERS DIRECTORY
  // =========================================================
  useEffect(() => {
    if (selectedUserId) return;

    const fetchDirectoryData = async () => {
      setLoadingUsers(true);
      try {
        const token = localStorage.getItem('token');
        const headers = {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        };

        const params = new URLSearchParams({
          search,
          page: (page + 1).toString(),
          limit: rowsPerPage.toString()
        }).toString();

        const [metricsRes, usersRes] = await Promise.all([
          fetch(`${API_BASE_URL}/admin/analytics-summary?days=7`, { headers }),
          fetch(`${API_BASE_URL}/admin/users-directory?${params}`, { headers })
        ]);

        if (metricsRes.ok) {
          const metricsData = await metricsRes.json();
          setMetrics(prev => ({ ...prev, ...metricsData }));
        }

        if (usersRes.ok) {
          const usersData = await usersRes.json();
          setUsers(usersData.results || []);
          setTotalUsers(usersData.totalUsers || 0);
        }
      } catch (error) {
        console.error("Error fetching users directory:", error);
      } finally {
        setLoadingUsers(false);
      }
    };

    const delayDebounceFn = setTimeout(() => {
      fetchDirectoryData();
    }, 400);

    return () => clearTimeout(delayDebounceFn);
  }, [search, page, rowsPerPage, selectedUserId]);
  // =========================================================
  // 2. FETCH SPECIFIC USER TIMELINE SUB-PAGE DATA
  // =========================================================
  useEffect(() => {
    if (!selectedUserId) return;

    const fetchUserTimeline = async () => {
      setLoadingTimeline(true);
      try {
        const token = localStorage.getItem('token');
        const headers = {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        };

        const res = await fetch(`${API_BASE_URL}/admin/users/${selectedUserId}/timeline`, { headers });
        if (res.ok) {
          const data = await res.json();
          setUserData(data.user);
          setTimeline(data.timeline || []);
        }
      } catch (error) {
        console.error("Error fetching user timeline:", error);
      } finally {
        setLoadingTimeline(false);
      }
    };

    fetchUserTimeline();
  }, [selectedUserId]);
  // =========================================================
  // 3. SSE LIVE ACTIVE USERS STREAM
  // =========================================================
  useEffect(() => {
    const token = localStorage.getItem('token');
    const liveStream = new EventSource(`${API_BASE_URL}/admin/realtime-users?token=${token}`);

    liveStream.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        setMetrics(prev => ({ ...prev, realtimeUsers: data.onlineCount }));
      } catch (err) {
        console.error("Failed parsing SSE payload:", err);
      }
    };

    return () => liveStream.close();
  }, []);

  return (
    <Box sx={{ flexGrow: 1, p: 4, bgcolor: 'transparent', minHeight: '100vh' }}>
      
      {/* HEADER SECTION */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box display="flex" alignItems="center" gap={2}>
          {selectedUserId && (
            <IconButton 
              onClick={() => setSelectedUserId(null)} 
              sx={{ bgcolor: '#ffffff', boxShadow: 1, '&:hover': { bgcolor: '#f1f5f9' } }}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          <Box>
            <Typography variant="h4" fontWeight="700" color="text.primary">
              {selectedUserId ? "User Activity Timeline" : "Users Activity"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {selectedUserId 
                ? `Viewing historical actions and endpoint logs for ${userData?.name || 'User'}` 
                : "Real-time analytics and user directory overview"}
            </Typography>
          </Box>
        </Box>
        
        <Chip 
          label={`${metrics.realtimeUsers} Active Users Right Now`} 
          color="success" 
          variant="outlined"
          sx={{ fontWeight: '600', px: 1, bgcolor: '#f0fdf4', borderColor: '#bbf7d0' }}
        />
      </Box>

      {/* VIEW A: USERS DIRECTORY (MAIN PAGE) */}
      {!selectedUserId ? (
        <>
          {/* TOP METRICS CARDS */}
          <Grid container spacing={3} mb={4}>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={0} sx={{ borderRadius: 1, border: '1px solid #e2e8f0' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" fontWeight="500">DAU / MAU Ratio</Typography>
                  <Typography variant="h4" fontWeight="700" color="text.primary" sx={{ mt: 1 }}>
                    {metrics.dauMauRatio}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={0} sx={{ borderRadius: 1, border: '1px solid #e2e8f0' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" fontWeight="500">Avg Session Duration</Typography>
                  <Typography variant="h4" fontWeight="700" color="text.primary" sx={{ mt: 1 }}>
                    {metrics.avgSession}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={0} sx={{ borderRadius: 1, border: '1px solid #e2e8f0' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" fontWeight="500">New Registrations</Typography>
                  <Typography variant="h4" fontWeight="700" color="success.main" sx={{ mt: 1 }}>
                    +{metrics.newRegistrations}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={0} sx={{ borderRadius: 1, border: '1px solid #e2e8f0' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" fontWeight="500">User Retention Rate</Typography>
                  <Typography variant="h4" fontWeight="700" color="primary.main" sx={{ mt: 1 }}>
                    {metrics.retentionRate}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* SEARCH & REPLACED FILTER BAR */}
          <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2.5, border: '1px solid #e2e8f0' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search user by name, email, or mobile..."
                  value={search}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* USERS DIRECTORY TABLE */}
          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 1, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: '600', color: '#475569' }}>User Name</TableCell>
                  <TableCell sx={{ fontWeight: '600', color: '#475569' }}>Email / Mobile</TableCell>
                  <TableCell sx={{ fontWeight: '600', color: '#475569' }}>Latest Action</TableCell>
                  <TableCell sx={{ fontWeight: '600', color: '#475569' }}>Date & Time</TableCell>
                  <TableCell sx={{ fontWeight: '600', color: '#475569' }}>Status</TableCell>
                  <TableCell align="right" sx={{ fontWeight: '600', color: '#475569' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingUsers ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={30} />
                    </TableCell>
                  </TableRow>
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No registered users found matching your search.
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((row) => (
                    <TableRow key={row.id} hover sx={{ cursor: 'pointer' }} onClick={() => setSelectedUserId(row.id)}>
                      <TableCell sx={{ fontWeight: '600', color: '#1e293b' }}>{row.name}</TableCell>
                      <TableCell color="text.secondary">{row.emailOrMobile}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', color: '#334155' }}>{row.latestAction}</TableCell>
                      <TableCell color="text.secondary">{row.actionDateTime}</TableCell>
                      <TableCell>{getStatusChip(row.status)}</TableCell>
                      <TableCell align="right">
                        <Button 
                          size="small" 
                          variant="outlined" 
                          onClick={(e) => { e.stopPropagation(); setSelectedUserId(row.id); }}
                          sx={{ textTransform: 'none', borderRadius: 2 }}
                        >
                          View Timeline
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={totalUsers}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              sx={{ borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}
            />
          </TableContainer>
        </>
      ) : (
        /* VIEW B: SINGLE USER TIMELINE SUB-PAGE */
        <Box>
          {loadingTimeline ? (
            <Box display="flex" justifyContent="center" py={8}>
              <CircularProgress size={40} />
            </Box>
          ) : (
            <>
              {/* USER INFORMATION CARD */}
              <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: 1, border: '1px solid #e2e8f0', bgcolor: '#ffffff' }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item>
                    <Avatar sx={{ width: 64, height: 64, bgcolor: '#3b82f6', fontSize: '1.5rem', fontWeight: 'bold' }}>
                      {userData?.name ? userData.name.charAt(0).toUpperCase() : 'U'}
                    </Avatar>
                  </Grid>
                  <Grid item xs>
                    <Typography variant="h5" fontWeight="700" color="#0f172a">
                      {userData?.name || 'User Profile'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {userData?.email}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Box display="flex" gap={3}>
                      <Box textAlign="right">
                        <Typography variant="caption" color="text.secondary">User ID</Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: '600' }}>
                          {userData?.id}
                        </Typography>
                      </Box>
                      <Box textAlign="right">
                        <Typography variant="caption" color="text.secondary">Registered On</Typography>
                        <Typography variant="body2" fontWeight="600">
                          {userData?.registeredAt ? new Date(userData.registeredAt).toLocaleDateString() : 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              {/* TIMELINE EVENTS CHRONOLOGICAL LIST */}
              <Typography variant="h6" fontWeight="700" color="#1e293b" mb={2}>
                Activity History
              </Typography>

              {timeline.length === 0 ? (
                <Paper elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: 1, border: '1px solid #e2e8f0' }}>
                  <Typography color="text.secondary">No recorded activity timeline for this user.</Typography>
                </Paper>
              ) : (
                timeline.map((group, groupIdx) => (
                  <Box key={groupIdx} mb={4}>
                    <Typography variant="subtitle2" sx={{ fontWeight: '700', color: '#64748b', mb: 2, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {group.groupLabel}
                    </Typography>

                    <Box sx={{ pl: 2, borderLeft: '2px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {group.events.map((event) => (
                        <Paper 
                          key={event.id} 
                          elevation={0} 
                          sx={{ p: 2.5, borderRadius: 1.5, border: '1px solid #e2e8f0', position: 'relative', ml: 1 }}
                        >
                          {/* Timeline Node Bullet */}
                          <Box 
                            sx={{ 
                              position: 'absolute', 
                              left: -27, 
                              top: 20, 
                              width: 12, 
                              height: 12, 
                              borderRadius: '50%', 
                              bgcolor: event.color || '#3b82f6', 
                              border: '2px solid #ffffff' 
                            }} 
                          />

                          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                            <Box display="flex" alignItems="center" gap={1.5}>
                              <Typography variant="h6" fontSize="1.1rem">{event.icon || '📌'}</Typography>
                              <Box>
                                <Typography variant="subtitle1" fontWeight="700" color="#0f172a">
                                  {event.title}
                                </Typography>
                                <Chip 
                                  label={event.category} 
                                  size="small" 
                                  sx={{ bgcolor: '#f1f5f9', color: '#475569', fontWeight: '600', fontSize: '0.7rem', height: 20, mt: 0.5 }} 
                                />
                              </Box>
                            </Box>
                            
                            <Box display="flex" alignItems="center" gap={0.5} color="text.secondary">
                              <AccessTimeIcon fontSize="small" />
                              <Typography variant="caption" fontWeight="500">{event.time}</Typography>
                            </Box>
                          </Box>

                          <Typography variant="body2" color="#334155" sx={{ mt: 1.5, fontFamily: 'monospace', bgcolor: '#f8fafc', p: 1, borderRadius: 1.5 }}>
                            {event.description}
                          </Typography>

                          {event.ip && (
                            <Box display="flex" alignItems="center" gap={0.5} mt={1} color="text.secondary">
                              <ComputerIcon fontSize="inherit" />
                              <Typography variant="caption">IP: {event.ip}</Typography>
                            </Box>
                          )}
                        </Paper>
                      ))}
                    </Box>
                  </Box>
                ))
              )}
            </>
          )}
        </Box>
      )}
    </Box>
  );
}